import { useEffect } from 'react';
import { Zap, Loader2, AlertTriangle, Clock } from 'lucide-react';
import { useIntegrationStore } from '../../store/integrationStore';
import { usePersonStore } from '../../store/personStore';
import type { PersonDetail } from '../../types';
import { formatPhone } from '../../lib/formatPhone';

interface PersonBlastStatusProps {
  person: PersonDetail;
}

export const PersonBlastStatus = ({ person }: PersonBlastStatusProps) => {
  const {
    personBlastUser,
    isLoadingPersonStatus,
    personStatusError,
    fetchPersonBlastStatus,
    clearPersonBlastStatus,
  } = useIntegrationStore();
  const { updatePerson } = usePersonStore();

  useEffect(() => {
    if (person.email) {
      fetchPersonBlastStatus(person.email);
    }

    return () => {
      clearPersonBlastStatus();
    };
  }, [person.id, person.email, fetchPersonBlastStatus, clearPersonBlastStatus]);

  // Lazy sync: if Blast lookup found the user but CRM doesn't have blastUserId, auto-update
  useEffect(() => {
    if (personBlastUser && !person.blastUserId) {
      updatePerson(person.id, { blastUserId: String(personBlastUser.id) }).catch(() => {
        console.error('Failed to auto-sync blastUserId from Blast lookup');
      });
    }
  }, [personBlastUser, person.blastUserId, person.id, updatePerson]);

  return (
    <div className="card p-6">
      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
        Blast Status
      </h3>

      {/* Loading */}
      {isLoadingPersonStatus && (
        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking Blast...
        </div>
      )}

      {/* Error */}
      {!isLoadingPersonStatus && personStatusError && (
        <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 text-sm">
          <AlertTriangle className="h-4 w-4" />
          Could not check Blast
        </div>
      )}

      {/* No email */}
      {!isLoadingPersonStatus && !personStatusError && !person.email && (
        <p className="text-sm text-gray-400 dark:text-gray-500 italic">
          No email — cannot check Blast
        </p>
      )}

      {/* Not found in Blast, but converted — awaiting invite acceptance */}
      {!isLoadingPersonStatus && !personStatusError && person.email && !personBlastUser && person.convertedAt && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
              <Clock className="h-3 w-3" />
              Invite Sent
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Converted on {new Date(person.convertedAt).toLocaleDateString()}. Waiting for coach to accept invite and create Blast account.
          </p>
        </div>
      )}

      {/* Not found via email lookup, but has a known blastUserId — show linked status */}
      {!isLoadingPersonStatus && !personStatusError && !personBlastUser && person.blastUserId && !person.convertedAt && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
              <Zap className="h-3 w-3" />
              In Blast
            </span>
          </div>
          <div className="text-sm space-y-1.5">
            <div>
              <span className="text-gray-500 dark:text-gray-400">User ID: </span>
              <span className="text-gray-900 dark:text-gray-100 font-mono text-xs">
                {person.blastUserId}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Not found in Blast, not converted */}
      {!isLoadingPersonStatus && !personStatusError && person.email && !personBlastUser && !person.blastUserId && !person.convertedAt && (
        <p className="text-sm text-gray-400 dark:text-gray-500 italic">
          Not in Blast
        </p>
      )}

      {/* Found in Blast */}
      {!isLoadingPersonStatus && !personStatusError && personBlastUser && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
              <Zap className="h-3 w-3" />
              In Blast
            </span>
          </div>

          <div className="text-sm space-y-1.5">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Name: </span>
              <span className="text-gray-900 dark:text-gray-100">
                {personBlastUser.first_name} {personBlastUser.last_name}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">User ID: </span>
              <span className="text-gray-900 dark:text-gray-100 font-mono text-xs">
                {personBlastUser.id}
              </span>
            </div>
            {personBlastUser.phone && (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Phone: </span>
                <span className="text-gray-900 dark:text-gray-100">
                  {formatPhone(personBlastUser.phone)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
